/* Maison welcome popup. Disabled until the Shopify discount has been verified. */
(() => {
  'use strict';

  if (window.MaisonWelcomePopup) return;

  const defaults = Object.freeze({
    enabled: false,
    code: 'BRIDE10',
    codeVerified: false,
    termsVerified: false,
    termsText: '',
    delayMs: 10_000,
    suppressionDays: 14,
  });
  const preferenceKey = 'maison-welcome-popup-v1';
  const sessionKey = 'maison-welcome-popup-shown-v1';
  const consentSurfaces = [
    '[data-cookie-banner]:not([hidden])',
    '.maison-cookie:not([hidden])',
    '[data-cookie-dialog][open]',
    'dialog[open]',
    '[role="dialog"][aria-modal="true"]',
    '#wbeu-overlay',
    '[id*="cookie" i][role="dialog"]',
    '[class*="cookie" i][role="dialog"]',
  ].join(',');

  function safeStorageGet(name, key) {
    try { return window[name].getItem(key); } catch { return null; }
  }

  function safeStorageSet(name, key, value) {
    try { window[name].setItem(key, value); return true; } catch { return false; }
  }

  function isVisible(element) {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 &&
      element.getClientRects().length > 0;
  }

  function hasBlockingOverlay() {
    return [...document.querySelectorAll(consentSurfaces)].some(isVisible);
  }

  function readSuppression(now, days) {
    const raw = safeStorageGet('localStorage', preferenceKey);
    if (!raw) return false;
    try {
      const record = JSON.parse(raw);
      return Number.isFinite(record?.suppressedUntil) && record.suppressedUntil > now &&
        record.reason === 'dismissed-or-copied' && Number.isFinite(days);
    } catch { return false; }
  }

  function setSuppression(now, days) {
    safeStorageSet('localStorage', preferenceKey, JSON.stringify({
      reason: 'dismissed-or-copied', suppressedUntil: now + days * 24 * 60 * 60 * 1000,
    }));
  }

  function focusable(dialog) {
    return [...dialog.querySelectorAll('button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])')]
      .filter(element => isVisible(element));
  }

  function createDialog(code, preview, termsText) {
    const dialog = document.createElement('dialog');
    dialog.className = 'maison-welcome';
    dialog.dataset.maisonWelcomePopup = '';
    dialog.setAttribute('aria-labelledby', 'maison-welcome-title');
    dialog.setAttribute('aria-describedby', 'maison-welcome-copy maison-welcome-terms');
    dialog.innerHTML = `
      <div class="maison-welcome__panel">
        <button class="maison-welcome__close" type="button" aria-label="Willkommensfenster schließen" data-welcome-close>
          <span aria-hidden="true">×</span>
        </button>
        ${preview ? '<p class="maison-welcome__preview">Vorschau · Rabatt noch nicht verifiziert</p>' : ''}
        <p class="maison-welcome__eyebrow">Ein kleiner Gruß für euch</p>
        <h2 id="maison-welcome-title">Schön, dass du da bist!</h2>
        <p class="maison-welcome__discount"><strong>10 %</strong><span>Willkommensrabatt</span></p>
        <p id="maison-welcome-copy" class="maison-welcome__copy">Entdecke unsere Hochzeitsartikel und sichere dir 10 % Rabatt auf deine gesamte Bestellung.</p>
        <p class="maison-welcome__code-label">Dein Rabattcode</p>
        <p class="maison-welcome__code" data-welcome-code></p>
        <button class="maison-welcome__copy-button" type="button" data-welcome-copy>Rabattcode kopieren</button>
        <p id="maison-welcome-terms" class="maison-welcome__terms"></p>
        <p class="maison-welcome__status" role="status" aria-live="polite" data-welcome-status></p>
      </div>`;
    dialog.querySelector('[data-welcome-code]').textContent = code;
    dialog.querySelector('#maison-welcome-terms').textContent = termsText;
    return dialog;
  }

  async function copyText(text) {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.className = 'maison-welcome__copy-fallback';
    document.body.append(field);
    field.select();
    let copied = false;
    try { copied = document.execCommand('copy'); } finally { field.remove(); }
    return copied;
  }

  function init(options = {}) {
    const config = { ...defaults, ...options };
    if (!config.enabled || !config.codeVerified || !config.termsVerified || typeof config.termsText !== 'string' ||
        !config.termsText.trim() || typeof config.code !== 'string' ||
        !/^[A-Z0-9_-]{4,32}$/.test(config.code) || document.querySelector('[data-maison-welcome-popup]') ||
        /\/checkouts?(?:\/|$)/i.test(window.location.pathname)) return null;

    const now = Date.now();
    if (readSuppression(now, config.suppressionDays) || safeStorageGet('sessionStorage', sessionKey)) return null;

    let dialog;
    let timer;
    let disposed = false;
    let consumed = false;
    let previousFocus = null;
    const startedAt = now;
    const preview = Boolean(config.preview);
    const remaining = () => Math.max(0, config.delayMs - (Date.now() - startedAt));

    function scheduleTry() {
      if (disposed || consumed || dialog?.open) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = undefined;
        tryShow();
      }, remaining());
    }

    function close(reason = 'dismiss') {
      if (!dialog?.open) return;
      if (reason === 'dismiss' || reason === 'copied') setSuppression(Date.now(), config.suppressionDays);
      dialog.close(reason);
    }

    function tryShow() {
      if (disposed || consumed || dialog?.open) return;
      if (hasBlockingOverlay()) {
        return;
      }
      if (remaining() > 0) {
        scheduleTry();
        return;
      }
      if (!dialog) {
        dialog = createDialog(config.code, preview, config.termsText);
        document.body.append(dialog);
        dialog.addEventListener('close', () => {
          if (dialog.returnValue === 'dismiss' || dialog.returnValue === 'copied') {
            safeStorageSet('sessionStorage', sessionKey, '1');
          }
          if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
          document.removeEventListener('keydown', onKeydown, true);
          window.removeEventListener('pagehide', onPageHide);
        }, { once: true });
        dialog.querySelector('[data-welcome-close]').addEventListener('click', () => close('dismiss'));
        dialog.querySelector('[data-welcome-copy]').addEventListener('click', async event => {
          const button = event.currentTarget;
          const status = dialog.querySelector('[data-welcome-status]');
          button.disabled = true;
          try {
            if (!await copyText(config.code)) throw new Error('copy unavailable');
            status.textContent = 'Der Code wurde kopiert.';
            button.textContent = 'Kopiert';
            setSuppression(Date.now(), config.suppressionDays);
            window.setTimeout(() => close('copied'), 700);
          } catch {
            status.textContent = 'Kopieren nicht möglich. Bitte markiere den Code und kopiere ihn manuell.';
            button.disabled = false;
          }
        });
        dialog.addEventListener('click', event => {
          if (event.target === dialog) close('dismiss');
        });
        dialog.addEventListener('cancel', event => {
          event.preventDefault();
          close('dismiss');
        });
      }
      consumed = true;
      safeStorageSet('sessionStorage', sessionKey, '1');
      previousFocus = document.activeElement;
      dialog.showModal();
      dialog.dataset.previousFocus = previousFocus?.id || '';
      dialog.querySelector('[data-welcome-close]').focus();
      document.addEventListener('keydown', onKeydown, true);
      window.addEventListener('pagehide', onPageHide, { once: true });
    }

    function onKeydown(event) {
      if (!dialog?.open || event.key !== 'Tab') return;
      const items = focusable(dialog);
      if (!items.length) { event.preventDefault(); dialog.focus(); return; }
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    function onPageHide() {
      disposed = true;
      window.clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('storage', onStorage);
    }

    function onStorage(event) {
      if (event.key === preferenceKey && dialog?.open) close('dismiss');
    }

    const observer = new MutationObserver(() => {
      if (!dialog?.open && !consumed && !hasBlockingOverlay()) scheduleTry();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['open', 'hidden', 'aria-hidden', 'class', 'style'] });
    window.addEventListener('storage', onStorage);
    scheduleTry();
    return Object.freeze({ destroy() {
      disposed = true;
      window.clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('storage', onStorage);
      if (dialog?.open) dialog.close();
      dialog?.remove();
    } });
  }

  window.MaisonWelcomePopup = Object.freeze({ init });
  const configured = window.maisonWelcomePopupConfig;
  if (configured) init(configured);
})();
